import { useState, useRef, useCallback, useEffect } from "react";
import { DndContext, DragEndEvent, DragOverlay, pointerWithin } from "@dnd-kit/core";
import { PlaybookNode } from "@/hooks/usePlaybooks";
import { buildTreeFromNodes, getConnections, TreeNode } from "@/lib/treeLayout";
import { TreeNodeCard } from "./TreeNodeCard";
import { ConnectionLines } from "./ConnectionLines";
import { Button } from "@/components/ui/button";
import { ZoomIn, ZoomOut, Maximize2, AlertTriangle } from "lucide-react";
import { cn } from "@/lib/utils";

interface NodeCanvasProps {
  nodes: PlaybookNode[];
  onEditNode: (node: PlaybookNode) => void;
  onDeleteNode: (id: string) => void;
  onUpdatePosition?: (nodeId: string, x: number, y: number) => void;
}

const LAYOUT_CONFIG = {
  nodeWidth: 280,
  nodeHeight: 140,
  horizontalSpacing: 60,
  verticalSpacing: 100,
};

export function NodeCanvas({
  nodes,
  onEditNode,
  onDeleteNode,
  onUpdatePosition,
}: NodeCanvasProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  const [highlightedNode, setHighlightedNode] = useState<string | null>(null);
  const [activeNode, setActiveNode] = useState<PlaybookNode | null>(null);

  // Build tree layout
  const { nodeMap, canvasSize } = buildTreeFromNodes(nodes, LAYOUT_CONFIG);
  const connections = getConnections(nodeMap, LAYOUT_CONFIG);

  // Get orphan nodes (not connected to start)
  const orphanNodeIds = new Set<string>();
  nodeMap.forEach((tn, nodeId) => {
    if (tn.level === -1) {
      orphanNodeIds.add(nodeId);
    }
  });

  // Zoom controls
  const handleZoomIn = () => setZoom((z) => Math.min(z + 0.2, 2));
  const handleZoomOut = () => setZoom((z) => Math.max(z - 0.2, 0.3));
  const handleFitView = () => {
    if (!containerRef.current) return;
    const containerWidth = containerRef.current.clientWidth;
    const containerHeight = containerRef.current.clientHeight;
    const scaleX = containerWidth / canvasSize.width;
    const scaleY = containerHeight / canvasSize.height;
    const newZoom = Math.min(Math.max(Math.min(scaleX, scaleY) * 0.9, 0.3), 1);
    setZoom(newZoom);
    setPan({ x: 20, y: 20 });
  };

  // Pan handling
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (e.button === 1 || (e.button === 0 && e.altKey)) {
      setIsPanning(true);
      setPanStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
    }
  }, [pan]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (isPanning) {
      setPan({
        x: e.clientX - panStart.x,
        y: e.clientY - panStart.y,
      });
    }
  }, [isPanning, panStart]);

  const handleMouseUp = useCallback(() => {
    setIsPanning(false);
  }, []);

  // Wheel zoom
  const handleWheel = useCallback((e: React.WheelEvent) => {
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      const delta = e.deltaY > 0 ? -0.1 : 0.1;
      setZoom((z) => Math.min(Math.max(z + delta, 0.3), 2));
    }
  }, []);

  // Drag end handler
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, delta } = event;
    if (onUpdatePosition && delta.x !== 0 && delta.y !== 0) {
      const nodeData = active.data.current;
      if (nodeData) {
        const newX = nodeData.x + delta.x / zoom;
        const newY = nodeData.y + delta.y / zoom;
        onUpdatePosition(active.id as string, newX, newY);
      }
    }
    setActiveNode(null);
  };

  // Fit view on initial load
  useEffect(() => {
    const timer = setTimeout(handleFitView, 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="relative w-full h-[600px] border rounded-lg bg-muted/20 overflow-hidden">
      {/* Zoom controls */}
      <div className="absolute top-4 right-4 z-10 flex gap-2">
        <Button variant="outline" size="icon" onClick={handleZoomOut}>
          <ZoomOut className="h-4 w-4" />
        </Button>
        <Button variant="outline" size="sm" className="min-w-[60px]">
          {Math.round(zoom * 100)}%
        </Button>
        <Button variant="outline" size="icon" onClick={handleZoomIn}>
          <ZoomIn className="h-4 w-4" />
        </Button>
        <Button variant="outline" size="icon" onClick={handleFitView}>
          <Maximize2 className="h-4 w-4" />
        </Button>
      </div>

      {/* Legend */}
      {orphanNodeIds.size > 0 && (
        <div className="absolute top-4 left-4 z-10 flex items-center gap-2 bg-background/80 backdrop-blur px-3 py-2 rounded-lg border">
          <AlertTriangle className="h-4 w-4 text-warning" />
          <span className="text-sm text-muted-foreground">
            {orphanNodeIds.size} orphan node(s) not connected to tree
          </span>
        </div>
      )}

      {/* Canvas */}
      <div
        ref={containerRef}
        className={cn(
          "w-full h-full",
          isPanning && "cursor-grabbing"
        )}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onWheel={handleWheel}
      >
        <DndContext
          collisionDetection={pointerWithin}
          onDragStart={(e) => {
            const node = e.active.data.current?.node;
            if (node) setActiveNode(node);
          }}
          onDragEnd={handleDragEnd}
        >
          <div
            className="relative"
            style={{
              transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
              transformOrigin: "0 0",
              width: `${canvasSize.width}px`,
              height: `${canvasSize.height}px`,
            }}
          >
            {/* Connection lines */}
            <ConnectionLines
              connections={connections}
              highlightedNode={highlightedNode}
            />

            {/* Node cards */}
            {Array.from(nodeMap.entries()).map(([nodeId, treeNode]) => (
              <TreeNodeCard
                key={nodeId}
                node={treeNode.node}
                x={treeNode.x}
                y={treeNode.y}
                isHighlighted={highlightedNode === nodeId}
                isOrphan={orphanNodeIds.has(nodeId)}
                onEdit={onEditNode}
                onDelete={onDeleteNode}
                onHover={setHighlightedNode}
              />
            ))}
          </div>

          <DragOverlay>
            {activeNode && (
              <div className="opacity-50">
                <TreeNodeCard
                  node={activeNode}
                  x={0}
                  y={0}
                  onEdit={() => {}}
                  onDelete={() => {}}
                  onHover={() => {}}
                />
              </div>
            )}
          </DragOverlay>
        </DndContext>
      </div>

      {/* Help text */}
      <div className="absolute bottom-4 left-4 text-xs text-muted-foreground">
        Alt+drag or middle-click to pan • Ctrl+scroll to zoom • Drag nodes to reposition
      </div>
    </div>
  );
}
